import { ApiDomainHoster, ApiDomainStatus } from "@incendium/api";
import { sortDomainsByWwwThenName } from "features/subdomains/utils";
import { isWwwDomain } from "Helpers/urls";
import { useSubdomains } from "Hooks/useSubdomains";
import { ApiURL } from "config";
import { useMemo } from "react";

export interface FireflyScriptDomainOption {
  id: string;
  label: string;
  baseUrl: string;
  isWww: boolean;
}

export function useFireflyScriptDomainOptions() {
  const { subdomains } = useSubdomains();

  const scriptDomainOptions = useMemo((): FireflyScriptDomainOption[] => {
    const options: FireflyScriptDomainOption[] = [
      {
        id: "default",
        label: "Incendium API domain",
        baseUrl: `${ApiURL}/firefly`,
        isWww: false,
      },
    ];

    const customDomainOptions = (subdomains || [])
      .filter(
        (sd) =>
          sd.hoster === ApiDomainHoster.DOMAIN_HOSTER_CLOUDFLARE &&
          sd.status === ApiDomainStatus.DOMAIN_STATUS_READY &&
          !!sd.subdomain &&
          !!sd.domain &&
          !!sd.url,
      )
      .sort(sortDomainsByWwwThenName)
      .map((sd) => ({
        id: String(sd.id),
        label: sd.cnameName || sd.domain || String(sd.id),
        baseUrl: `${sd.url}/firefly`,
        isWww: isWwwDomain(sd.domain),
      }));

    return [...options, ...customDomainOptions];
  }, [subdomains]);

  const defaultScriptBaseUrl =
    scriptDomainOptions.find((o) => o.id !== "default")?.baseUrl ||
    scriptDomainOptions[0]?.baseUrl ||
    `${ApiURL}/firefly`;

  return { scriptDomainOptions, defaultScriptBaseUrl };
}

export function resolveScriptDomainOptionId(
  storedBaseUrl: string | undefined,
  options: FireflyScriptDomainOption[],
): string {
  if (!storedBaseUrl) {
    const firstCustom = options.find((o) => o.id !== "default");
    return firstCustom?.id ?? "default";
  }
  const normalized = storedBaseUrl.replace(/\/+$/, "");
  const match = options.find(
    (o) => normalized === o.baseUrl.replace(/\/+$/, ""),
  );
  return match?.id ?? "default";
}
